import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import BlackHole from "@/studio/components/BlackHole";
import Magnetic from "@/studio/components/Magnetic";

const ease = [0.16, 1, 0.3, 1];
const EMAIL = "hello@parabolum.digital";
const SOCIALS = ["LinkedIn", "X / Twitter", "GitHub", "Dribbble"];

export default function Contact({ onContact }) {
  return (
    <section
      id="contact"
      data-nav-dark
      className="relative min-h-screen overflow-hidden bg-[#030303] py-24 text-white md:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(70% 50% at 82% 0%, rgba(228,255,0,0.05), transparent 60%)",
        }}
      />
      {/* brand black hole — breathing, with inward particles */}
      <div className="pointer-events-none absolute right-[-8%] top-[4%] z-0 hidden h-[64vh] w-[48%] md:block">
        <BlackHole className="h-full w-full" glow particles particleRGB="255,255,255" parallax={18} />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[78vh] max-w-[1500px] flex-col px-5 md:px-10">
        <span className="label text-white/40">(08) — Contact</span>

        <div className="mt-auto max-w-4xl">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease }}
            className="ff-display text-5xl font-extrabold leading-[0.92] tracking-tight md:text-8xl"
          >
            Let&apos;s build something{" "}
            <span className="text-[#E4FF00]">exceptional.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease }}
            className="ff-body mt-7 max-w-xl text-base leading-relaxed text-white/55 md:text-lg"
          >
            Have an idea, project or question?
            <br />
            We&apos;d love to hear from you.
          </motion.p>

          <div className="mt-12 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
            <div className="space-y-7">
              <div>
                <div className="label text-white/40">Email</div>
                <a href={`mailto:${EMAIL}`} data-cursor="big" className="link-underline mt-2 inline-block text-2xl font-medium tracking-tight md:text-3xl">
                  {EMAIL}
                </a>
              </div>
              <div>
                <div className="label text-white/40">Socials</div>
                <div className="mt-3 flex flex-wrap gap-3">
                  {SOCIALS.map((s) => (
                    <a
                      key={s}
                      href="#"
                      onClick={(e) => e.preventDefault()}
                      data-cursor="big"
                      className="rounded-full border border-white/20 px-4 py-2 text-[13px] font-medium text-white/80 transition-colors hover:bg-white hover:text-black"
                    >
                      {s}
                    </a>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 animate-pulse rounded-full bg-[#E4FF00] ring-4 ring-[#E4FF00]/20" />
                <span className="text-[14px] text-white/60">
                  Currently accepting new projects · Response within 24h
                </span>
              </div>
            </div>

            <Magnetic strength={0.3}>
              <button
                onClick={onContact}
                data-cursor="big"
                className="btn-magnetic group flex items-center gap-3 rounded-full bg-[#E4FF00] px-8 py-5 text-[16px] font-semibold text-black"
              >
                <span className="btn-label">Start a project</span>
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black text-white transition-transform duration-500 group-hover:translate-x-1">
                  →
                </span>
              </button>
            </Magnetic>
          </div>
        </div>

        {/* footer */}
        <div className="relative mt-24 grid items-start gap-10 border-t border-white/10 pt-10 md:grid-cols-12">
          {/* brand */}
          <div className="md:col-span-5">
            <div className="ff-display text-2xl font-extrabold tracking-tight">
              PARABOLUM<span className="text-[#E4FF00]">.</span>
            </div>
            <p className="ff-body mt-4 max-w-sm text-[14.5px] leading-relaxed text-white/45">
              Premium web experiences, applications and AI systems.
              <br />
              Built in Czechia.
            </p>
            <a
              href={`mailto:${EMAIL}`}
              data-testid="footer-email"
              data-cursor="hover"
              className="ff-body mt-6 inline-block text-[14px] text-white/70 transition-colors duration-500 hover:text-white"
            >
              {EMAIL}
            </a>
          </div>

          {/* spacer */}
          <div className="hidden md:col-span-3 md:block" />

          {/* status block */}
          <div className="md:col-span-4">
            <span className="ff-ui text-[11px] uppercase tracking-[0.32em] text-white/40">
              Status
            </span>
            <dl className="mt-4 grid grid-cols-3 gap-x-6 gap-y-3">
              <div>
                <dt className="ff-ui text-[10px] uppercase tracking-[0.22em] text-white/30">
                  Version
                </dt>
                <dd className="ff-display mt-1 text-[15px] text-white/85">1.0</dd>
              </div>
              <div>
                <dt className="ff-ui text-[10px] uppercase tracking-[0.22em] text-white/30">
                  Updated
                </dt>
                <dd className="ff-display mt-1 text-[15px] text-white/85">
                  June 2026
                </dd>
              </div>
              <div>
                <dt className="ff-ui text-[10px] uppercase tracking-[0.22em] text-white/30">
                  Status
                </dt>
                <dd className="mt-1 flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#E4FF00]" />
                  <span className="ff-display text-[15px] text-white/85">
                    Stable
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* bottom strip */}
        <div className="relative mt-10 flex items-center justify-between border-t border-white/[0.04] pt-6">
          <span className="ff-body text-[12px] text-white/35">
            © {new Date().getFullYear()} PARABOLUM
          </span>
          <Link
            to="/admin/login"
            aria-label="Admin"
            data-testid="footer-admin-link"
            className="flex h-8 w-8 items-center justify-center rounded-full border border-white/[0.08] text-white/25 transition-colors duration-500 hover:border-white/30 hover:text-white/70"
          >
            <Lock size={13} />
          </Link>
        </div>
      </div>
    </section>
  );
}
